/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, FormControl, Grid, Heading, HFlow, Icon, VFlow } from 'bold-ui'
import { useErrorHandler } from 'components/error/PromiseErrorBoundary'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { TipoGrupoParticipanteAtividadeColetivaEnum } from '../../../graphql/types.generated'
import { useConfirmRemoverGrupo } from '../grupos/hooks/useConfirmRemoverGrupo'
import {
  AtividadeColetivaGrupoItemModel,
  AtividadeColetivaGruposSelectField,
} from './AtividadeColetivaGrupoSelectField'

type AtividadeColetivaGruposFieldProps = {
  name: MetaPath<AtividadeColetivaGrupoItemModel | null>
  tipoGrupo: TipoGrupoParticipanteAtividadeColetivaEnum
  onAdicionarGrupo(value: AtividadeColetivaGrupoItemModel): void
  onRemoverGrupo(value: AtividadeColetivaGrupoItemModel): void
}

export default function AtividadeColetivaGruposField(props: Readonly<AtividadeColetivaGruposFieldProps>) {
  const { name, tipoGrupo, onAdicionarGrupo, onRemoverGrupo } = props
  const selectedGrupoPrefix = 'new-'
  const confirmRemoverGrupo = useConfirmRemoverGrupo()
  const handleRejection = useErrorHandler()

  const {
    input: { value: addedGrupo, onChange: setAddedGrupo },
  } = useField<AtividadeColetivaGrupoItemModel | null>(name.absolutePath(), { subscription: { value: true } })

  const {
    input: { value: selectedGrupo, onChange: setSelectedGrupo },
  } = useField<AtividadeColetivaGrupoItemModel | null>(`${selectedGrupoPrefix}${name.absolutePath()}`, {
    subscription: { value: true },
  })

  const hasAddedGrupo = !!addedGrupo

  const handleAdicionarGrupo = () => {
    if (!selectedGrupo) return

    onAdicionarGrupo(selectedGrupo)

    setAddedGrupo(selectedGrupo)
    setSelectedGrupo(null)
  }

  const handleRemoverGrupo = () => {
    if (!hasAddedGrupo) return

    confirmRemoverGrupo()
      .then((confirmResult) => {
        if (!confirmResult) return

        onRemoverGrupo(addedGrupo)

        setAddedGrupo(null)
        setSelectedGrupo(null)
      })
      .catch(handleRejection)
  }

  return (
    <Grid gap={1}>
      <Cell size={12}>
        <Heading level={2} style={styles.title}>
          Grupos
        </Heading>

        <VFlow vSpacing={1}>
          <HFlow>
            Selecione um grupo de atividade coletiva para importar automaticamente os participantes associados ao grupo
            para este registro. Alterações feitas na lista de participantes deste registro não impactarão a configuração
            original do grupo. Participantes adicionais também podem ser inseridos manualmente.
          </HFlow>
        </VFlow>
      </Cell>

      <Cell size={6}>
        <AtividadeColetivaGruposSelectField
          name={hasAddedGrupo ? name.absolutePath() : `${selectedGrupoPrefix}${name.absolutePath()}`}
          label='Grupos de atividade coletiva'
          tipoGrupo={tipoGrupo}
          disabled={hasAddedGrupo}
        />
      </Cell>

      <Cell size={3}>
        <FormControl label={<span>&nbsp;</span>}>
          {hasAddedGrupo ? (
            <Button kind='danger' skin='outline' size='small' onClick={handleRemoverGrupo}>
              <HFlow hSpacing={0.5}>
                <Icon icon='timesDefault' />
                Remover grupo
              </HFlow>
            </Button>
          ) : (
            <Button kind='primary' skin='outline' size='small' disabled={!selectedGrupo} onClick={handleAdicionarGrupo}>
              Adicionar grupo
            </Button>
          )}
        </FormControl>
      </Cell>
    </Grid>
  )
}

const styles = {
  title: css`
    margin-bottom: 0.5rem;
  `,
}
